/*
 * Arrows and progress bar for the root-cause card row.
 *
 * The swipe itself is CSS (`overflow-x` + `scroll-snap`), so this adds nothing to the gesture
 * path and the row still works with the script blocked. All this does is drive the two arrows,
 * disable them at the ends, and size the progress fill from the scroll position.
 *
 * Reads layout in a rAF and only on scroll/resize, so a fast swipe doesn't thrash.
 */

class VpRootCause extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rc-track]');
    this.fill = this.querySelector('[data-rc-fill]');
    this.prev = this.querySelector('[data-rc-prev]');
    this.next = this.querySelector('[data-rc-next]');
    if (!this.track) return;

    this.prev?.addEventListener('click', () => this.#step(-1));
    this.next?.addEventListener('click', () => this.#step(1));
    this.track.addEventListener('scroll', () => this.#schedule(), { passive: true });

    this.observer = new ResizeObserver(() => this.#schedule());
    this.observer.observe(this.track);
    this.#schedule();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  /** Width of one card plus the gap, so an arrow advances exactly one card. */
  get #stride() {
    const cards = this.track.children;
    if (cards.length < 2) return this.track.clientWidth;
    return cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left;
  }

  #step(direction) {
    this.track.scrollBy({ left: this.#stride * direction, behavior: 'smooth' });
  }

  #schedule() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = scrollWidth - clientWidth;

    // Fill width is the share of the row on screen; it slides rather than growing, so it reads
    // as "where am I" rather than "how much have I read".
    const visible = clientWidth / scrollWidth;
    const progress = max > 0 ? scrollLeft / max : 0;
    this.style.setProperty('--vp-rc-progress', `${Math.min(100, visible * 100).toFixed(2)}%`);
    this.style.setProperty('--vp-rc-progress-x', `${(progress * (1 / visible - 1) * 100).toFixed(2)}%`);

    // The row is padded so a card can sit flush with the screen edge, which means "at the
    // start" is one padding in, not 0. Plus 1px of slack for sub-pixel scroll positions.
    const startInset = parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
    if (this.prev) this.prev.disabled = scrollLeft <= startInset + 1;
    if (this.next) this.next.disabled = scrollLeft >= max - 1;
  }
}

if (!customElements.get('vp-root-cause')) {
  customElements.define('vp-root-cause', VpRootCause);
}
